import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import Loader from "../../../components/loader/loader";
import BreadCrumpBack from "../../../components/navigator/back";
import Snackbar from "../../../components/snackbar/snackbar";
import { getInternshipApplicantProfile } from "../user.api";
import moment from "moment";
import "./style.css";

export default function InternshipApplicantProfile() {
  const { id, proposalId } = useParams();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const servInstance = process.env.REACT_APP_BASE_URL || "";
  let result = servInstance ? servInstance.substring(0, servInstance?.length - 4) : "";

  useEffect(() => {
    if (id && proposalId) {
      fetchProfile();
    }
  }, [id, proposalId]);

  const fetchProfile = () => {
    setLoading(true);
    getInternshipApplicantProfile(id, proposalId)
      .then((res) => {
        if (res?.success) {
          setData(res.data);
        } else {
          setSnackMessage(res?.message || "Failed to load student profile");
          setIsSnackError(true);
          setSnackOpen(true);
        }
      })
      .catch((err) => {
        console.error("Error fetching student profile:", err);
        setSnackMessage("Error loading student profile");
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const snackbarClose = () => {
    setSnackOpen(false);
  };

  const resolvePath = (path) => {
    if (!path) return null;
    // Check if it's already a full URL (handling the potential storage prefix issues)
    if (path.startsWith('http')) {
      // Handle cases like http://127.0.0.1:8000/storage/http://127.0.0.1:8000/...
      if (path.includes('/storage/http')) {
        return path.split('/storage/')[1];
      }
      return path;
    }
    return result + path;
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: "Pending", class: "pending" },
      confirmed: { label: "Confirmed", class: "approved" },
      join_internship: { label: "Assigned to Company", class: "approved" },
      allotted: { label: "Assigned to Company", class: "approved" },
      doing: { label: "Internship in Progress", class: "approved" },
      evaluation: { label: "Evaluation Initiated", class: "approved" },
      evaluated: { label: "Evaluation Completed", class: "approved" },
      certified: { label: "Certificate Issued", class: "approved" },
      absorbed: { label: "Absorb into Company", class: "approved" },
      rejected: { label: "Rejected", class: "danger" },
    };
    return statusMap[status?.toLowerCase()] || { 
      label: status ? status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Unknown", 
      class: "pending" 
    };
  };

  if (loading) return <Loader open={true} />;

  if (!data) return (
    <div className="container mt-5">
      <BreadCrumpBack />
      <div className="alert alert-warning mt-3">No profile data found.</div>
    </div>
  );

  const { student_profile, ranking_info, internship_request, proposal_info } = data;
  const personal = student_profile?.personal_info;
  const statusInfo = getStatusInfo(proposal_info?.status);
  const uploadedFiles = student_profile?.uploaded_files || {};

  return (
    <div className="applicant-profile-page">
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />

      <div className="dash-banner">
        <div className="tit-txt">Applicant Profile</div>
      </div>

      <BreadCrumpBack />

      <div className="container-fluid mt-4 mb-5 px-md-5 text-start">
        <div className="d-flex justify-content-between align-items-center mb-5">
          <h5 className="fw-bold text-dark mb-0">Student Details</h5>
          <button className="btn btn-minimal-outline btn-sm" onClick={() => window.close()}>
            Close Page
          </button>
        </div>

        <div className="row g-4">
          {/* Sidebar */}
          <div className="col-lg-3 mb-4">
            <div className="section-card-minimal">
              <div className="section-header">Summary</div>
              <div className="card-body p-4 text-center">
                <div className="mb-4">
                  {uploadedFiles.photo ? (
                    <img src={resolvePath(uploadedFiles.photo)} alt="Profile" className="profile-img-minimal mx-auto" />
                  ) : (
                    <div className="profile-img-minimal bg-light d-inline-flex align-items-center justify-content-center mx-auto">
                      <span className="h1 text-muted mb-0">{personal?.full_name?.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <h5 className="fw-bold mb-1">{personal?.full_name}</h5>
                <p className="small text-muted mb-4 opacity-75">ID: {personal?.id}</p>

                <div className="text-start">
                  <div className="info-item-minimal mb-3 border-bottom pb-2">
                    <label>Application Status</label>
                    <span className={`status-badge-minimal ${statusInfo.class} d-inline-flex align-items-center mt-1`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <div className="info-item-minimal mb-3 border-bottom pb-2">
                    <label>Email Address</label>
                    <span className="text-break">{personal?.email}</span>
                  </div>
                  <div className="info-item-minimal mb-3">
                    <label>Contact Number</label>
                    <span>{personal?.phone_no}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-top">
                  <div className="row g-2 mb-4">
                    <div className="col-6">
                      <div className="p-2 border rounded bg-light text-center">
                        <label className="x-small text-muted d-block uppercase-heading">Rank</label>
                        <span className="fw-bold text-primary">#{ranking_info?.effective_rank || "--"}</span>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-2 border rounded bg-light text-center">
                        <label className="x-small text-muted d-block uppercase-heading">Score</label>
                        <span className="fw-bold text-success">{ranking_info?.latest_score || "--"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="d-grid gap-2">
                    {uploadedFiles.resume && (
                      <a href={resolvePath(uploadedFiles.resume)} target="_blank" rel="noreferrer" className="btn btn-minimal-dark w-100">
                        <i className="fa fa-file-pdf-o me-2"></i> View Resume
                      </a>
                    )}
                    {uploadedFiles.recommendation_letter && (
                      <a href={resolvePath(uploadedFiles.recommendation_letter)} target="_blank" rel="noreferrer" className="btn btn-minimal-outline w-100">
                        <i className="fa fa-file-text-o me-2"></i> Rec. Letter
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Institution Section */}
            <div className="section-card-minimal">
              <div className="section-header">Institution</div>
              <div className="card-body p-4">
                {student_profile?.institution?.length > 0 ? (
                  student_profile.institution.map((inst, idx) => (
                    <div key={idx} className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        {inst.logo && (
                          <img src={resolvePath(inst.logo)} alt="Logo" style={{ width: '40px', height: '40px', objectFit: 'contain' }} className="rounded border" />
                        )}
                        <div className="fw-bold text-dark small">{inst.institution_name}</div>
                      </div>
                      <div className="info-item-minimal mb-2">
                        <label>Ref No</label>
                        <span className="small">{inst.unique_application_ref_no || "N/A"}</span>
                      </div>
                      <div className="info-item-minimal">
                        <label>Location</label>
                        <span className="small d-block">{inst.address}</span>
                      </div>
                    </div>
                  ))
                ) : <div className="text-muted small italic">No institution details</div>}
              </div>
            </div>

            {/* Languages Section */}
            <div className="section-card-minimal">
              <div className="section-header">Languages</div>
              <div className="card-body p-4">
                <div className="d-flex flex-wrap gap-2">
                  {student_profile?.languages?.length > 0 ? (
                    student_profile.languages.map((lang, idx) => (
                      <span key={idx} className="badge-target-elite">
                        <i className="fa fa-language me-2"></i> {lang.language}
                      </span>
                    ))
                  ) : <span className="text-muted small">None listed</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-lg-9">
            {/* Personal Info Grid */}
            <div className="section-card-minimal">
              <div className="section-header">Personal Information</div>
              <div className="info-grid-minimal">
                <div className="info-item-minimal">
                  <label>Full Name</label>
                  <span>{personal?.full_name}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Gender</label>
                  <span>{personal?.gender || "N/A"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Date of Birth</label>
                  <span>{personal?.date_of_birth ? moment(personal.date_of_birth).format("DD MMM YYYY") : "N/A"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Nationality</label>
                  <span>{personal?.nationality || "Indian"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Kerala Native</label>
                  <span className={personal?.is_kerala_native ? "text-success" : "text-muted"}>
                    {personal?.is_kerala_native ? "Yes" : "No"}
                  </span>
                </div>
                <div className="info-item-minimal">
                  <label>Course Status</label>
                  <span className="text-capitalize">{personal?.course_completion_status || "N/A"}</span>
                </div>
                <div className="info-item-minimal col-span-2">
                  <label>Residential Address</label>
                  <span>{personal?.address || "N/A"}</span>
                </div>
                <div className="info-item-minimal col-span-2">
                  <label>Member Since</label>
                  <span>{moment(personal?.created_at).format("DD MMM YYYY, HH:mm A")}</span>
                </div>
              </div>
            </div>

            {/* Academic Qualifications */}
            <div className="section-card-minimal">
              <div className="section-header">Educational Qualifications</div>
              <div className="card-body p-0">
                {student_profile?.qualifications?.length > 0 ? (
                  student_profile.qualifications.map((qual, idx) => (
                    <div key={idx} className={`p-4 ${idx !== student_profile.qualifications.length - 1 ? 'border-bottom' : ''}`}>
                      <div className="d-flex justify-content-between align-items-start mb-4">
                        <div className="applicant-meta">
                          <div className="h6 fw-bold text-dark mb-1">{qual.qualification_type?.name} in {qual.qualification?.name}</div>
                          <div className="text-primary small fw-bold">{qual.stream?.name}</div>
                          {/* <div className="small text-muted mt-2">
                            <i className="fa fa-university me-1"></i> {qual.institution_name || qual.university || "Institution Details N/A"}
                          </div> */}
                        </div>
                        <div className="text-end">
                          {qual.uploaded_files?.certificate && (
                            <button
                              className="btn btn-minimal-outline btn-sm mb-2"
                              onClick={() => window.open(resolvePath(qual.uploaded_files.certificate), "_blank")}
                            >
                              <i className="fa fa-certificate me-2"></i> View Certificate
                            </button>
                          )}
                          <div className="p-2 px-3 bg-light rounded border small">
                            <span className="text-muted fw-bold x-small uppercase-heading d-block">Overall Score</span>
                            <span className="h6 fw-bold text-dark mb-0">{qual.cgpa || (qual.marksheets && qual.marksheets[0]?.cgpa) || "--"} CGPA</span>
                          </div>
                        </div>
                      </div>

                      {qual.marksheets && qual.marksheets.length > 0 && (
                        <div className="mt-4">
                          <div className="fw-bold x-small text-uppercase mb-3 px-1 text-muted letter-spacing-1">Semester Breakdown & Marksheets</div>
                          <div className="row g-3">
                            {qual.marksheets.map((mark, mIdx) => (
                              <div key={mIdx} className="col-md-6">
                                <div className="p-3 bg-white border rounded shadow-sm d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="small fw-bold">{mark.period_name || `Semester ${mark.period_number}`}</div>
                                    <div className="text-primary fw-bold">{mark.cgpa} CGPA</div>
                                    {mark.grade && <div className="x-small text-muted mt-1">Grade: {mark.grade}</div>}
                                    {mark.marks_obtained && (
                                      <div className="x-small text-muted">Marks: {mark.marks_obtained}/{mark.total_marks}</div>
                                    )}
                                  </div>
                                  {mark.marksheet_file && (
                                    <button
                                      className="btn btn-minimal-outline btn-xs"
                                      title="Download/View Marksheet"
                                      onClick={() => window.open(resolvePath(mark.marksheet_file), "_blank")}
                                    >
                                      <i className="fa fa-download"></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center p-5 text-muted italic">No qualification details found</div>
                )}
              </div>
            </div>

            {/* Internship Context */}
            {/* <div className="section-card-minimal">
              <div className="section-header">Internship Request Context</div>
              <div className="card-body p-4">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="info-item-minimal mb-4">
                      <label>Preferred Discipline</label>
                      <span className="h6 text-primary fw-bold">{internship_request?.internship_discipline}</span>
                    </div>
                    <div className="info-item-minimal mb-4">
                      <label>Request Description</label>
                      <span className="small text-muted d-block line-height-base">{internship_request?.brief_description}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="bg-dark-soft p-3 rounded">
                          <label className="x-small d-block mb-1 text-muted">Student Choice #</label>
                          <span className="h5 mb-0 fw-bold">{proposal_info?.preference_order}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="bg-dark-soft p-3 rounded">
                          <label className="x-small d-block mb-1 text-muted">Intake Positions</label>
                          <span className="h5 mb-0 fw-bold">{internship_request?.number_of_students}</span>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="bg-dark-soft p-3 rounded border-start border-4 border-primary">
                          <label className="x-small d-block mb-1 text-muted">Target Internship Period</label>
                          <span className="small fw-bold">
                            {internship_request?.preferred_start_date ? moment(internship_request.preferred_start_date).format("DD MMM YYYY") : "N/A"}
                            <span className="mx-2 text-muted">→</span>
                            {internship_request?.preferred_end_date ? moment(internship_request.preferred_end_date).format("DD MMM YYYY") : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}
